import { useCallback, useEffect } from 'react';
import i18next from 'i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useGetIdentityClientQuery, type IdentityClient } from 'src/api/authApi';
import type { ErrorLocationState } from 'src/features/error/errorCodes';
import { useSetLocale } from 'src/hooks/useSetLocale';
import { routePathConfig } from 'src/routes/config';
import { FIRST_PARTY_CLIENT_ID, FIRST_PARTY_REDIRECT_URI, OAUTH_PARAMS } from 'src/constants/oauth';

interface UseClientVerificationResult {
  client: IdentityClient['client'] | undefined;
  csrfCode: string | undefined;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  refreshCsrfCode: () => Promise<string | null>;
}

export const useClientVerification = (): UseClientVerificationResult => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlClientId = searchParams.get(OAUTH_PARAMS.CLIENT_ID);
  const urlRedirectUri = searchParams.get(OAUTH_PARAMS.REDIRECT_URI);
  const locale = searchParams.get(OAUTH_PARAMS.LOCALE) || i18next.language;

  // When the URL has no client_id (the first-party account management center
  // path), fall back to narrow_gate's own registered client identity so the
  // identity → authorize CSRF chain still fires. Keeping the fallback here
  // keeps a single code path for both flows.
  const clientId = urlClientId || FIRST_PARTY_CLIENT_ID;
  const redirectUri = urlRedirectUri || FIRST_PARTY_REDIRECT_URI;

  const queryResult = useGetIdentityClientQuery({ clientId, redirectUri, locale });

  const refreshCsrfCode = useCallback(async (): Promise<string | null> => {
    const refreshed = await queryResult.refetch();
    return refreshed.data?.csrfCode ?? null;
  }, [queryResult]);

  useSetLocale(queryResult.data?.client?.locale ?? locale);

  useEffect(() => {
    if (queryResult.isError && location.pathname !== routePathConfig.error) {
      const errorState: ErrorLocationState = {
        code: 'INVALID_CLIENT',
        returnUri: urlRedirectUri || undefined,
      };
      navigate(routePathConfig.error, { state: errorState });
    }
  }, [queryResult.isError, navigate, location.pathname, urlRedirectUri]);

  return {
    client: queryResult.data?.client,
    csrfCode: queryResult.data?.csrfCode,
    isLoading: queryResult.isLoading || queryResult.isFetching,
    isSuccess: queryResult.isSuccess,
    isError: queryResult.isError,
    refreshCsrfCode,
  };
};
